import React, { FunctionComponent } from "react";
import { ApexOptions } from "apexcharts";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import Donut from "../../Charts/Donut";
import colorPalette from "../colorPalette/colorPalette";
import {
  Backdrop,
  ModalWrapper,
  Title,
  TitleText,
  CloseButton,
  Body,
  Legend,
} from "../styles/legend-modal-styles";

interface ISpendPerSectorModalProps {
  currencySymbol: string;
  spendPerSector: {
    sectors?: Array<string>;
    values?: Array<number>;
  };
  toggleModal: () => void;
}

interface ISpendPerSectorOptionsProps extends ApexOptions {
  series: Array<number>;
  options: ApexOptions;
}

interface ILegendOptionsProps {
  series: Array<number>;
  options: ApexOptions;
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
  globals: {
    seriesTotals: {
      reduce: (a, b) => number;
    };
  };
}

export const SpendPerSectorModal: FunctionComponent<
  ISpendPerSectorModalProps
> = ({ currencySymbol, spendPerSector, toggleModal }) => {
  const spendPerSectorOptions: ISpendPerSectorOptionsProps = {
    series: spendPerSector.values,
    options: {
      chart: {
        width: 380,
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 4,
          opacity: 0.1,
        },
      },
      colors: colorPalette(spendPerSector.sectors.length),
      labels: spendPerSector.sectors,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          customScale: 1,
          offsetX: 0,
          offsetY: 0,
          expandOnClick: true,
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 10,
          },
          donut: {
            size: "65%",
            background: "transparent",
            labels: {
              show: true,
              value: {
                show: true,
                fontSize: "25px",
                fontFamily: "Komet, sans-serif",
                color: "#333",
                offsetY: 6,
                formatter: (val) => val,
              },
              total: {
                show: true,
                showAlways: true,
                label: "Total",
                fontSize: "18px",
                fontFamily: "Komet, sans-serif",
                color: "#333",
                formatter: (w: IFormatterProps) => {
                  const value = w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                  return `${currencySymbol}${value}`;
                },
              },
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontFamily: "Komet, sans-serif",
        },
        x: {
          formatter: (seriesName) => seriesName as unknown as string,
        },
        y: {
          formatter: (value) => `${currencySymbol}${value}`,
        },
      },
    },
  };

  const legendOptions: ILegendOptionsProps = {
    series: spendPerSector.values,
    options: {
      colors: colorPalette(spendPerSector.sectors.length),
      labels: spendPerSector.sectors,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        horizontalAlign: "left",
        formatter: (name: string, { seriesIndex, w }: IFormatterProps) =>
          `${name} - ${w.config.series[seriesIndex] as number}`,
        itemMargin: {
          horizontal: 4,
          vertical: 4,
        },
        position: "bottom",
        containerMargin: {
          top: 0,
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        width: "100%",
        height: 100,
      },
    },
  };

  return (
    <Backdrop className="animated fadeIn">
      <ModalWrapper className="animated delay-1 fadeInDown">
        <Title>
          <TitleText>Spend Per Sector</TitleText>
          <CloseButton onClick={() => toggleModal()}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </Title>
        <Body className="animated fadeIn delay-5">
          <Donut
            graphOptions={spendPerSectorOptions}
            dataTestId="spend-per-sector-chart"
          />
          <Legend>
            <Donut graphOptions={legendOptions} />
          </Legend>
        </Body>
      </ModalWrapper>
    </Backdrop>
  );
};

SpendPerSectorModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  spendPerSector: PropTypes.shape({
    sectors: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

interface IReduxState {
  employerAnalytics: {
    currencySymbol?: string;
    chartsData: {
      spendPerSector: {
        sectors?: Array<string>;
        values?: Array<number>;
      };
    };
  };
}

const mapStateToProps = (state: IReduxState) => ({
  currencySymbol: state.employerAnalytics.currencySymbol,
  spendPerSector: state.employerAnalytics.chartsData.spendPerSector,
});

export default connect(mapStateToProps, {})(SpendPerSectorModal);
